/**
 * ControlPanel API (`/chimes`) クライアント (要件 3・4)。
 *
 * 放送開始 / 終了時に鳴らすチャイム音マスタ (ChimeMaster) を CRUD する。
 * - すべての呼び出しに `Authorization: Bearer <Cognito ID Token>` を付与する。
 * - エンドポイントは `AppConfig.apiUrl` をベースとする。
 * - 音源は MP3 を base64 で送受信する (`fileToBase64` / `base64ToAudioUrl` を再利用)。
 */
import type { AppConfig } from '../config';

import { fileToBase64 } from './broadcastPoints';
import { ApiError, joinUrl } from './common';
import type {
  ApiErrorBody,
  Chime,
  ChimeAudioResponse,
  ChimeCreateRequest,
  ChimeDeleteResponse,
  ChimeListResponse,
  ChimeUpdateRequest,
} from './types';

export interface ChimesClientOptions {
  /** Cognito ID トークンを取得する関数 (期限切れ時は AuthProvider 側で自動更新済み) */
  getIdToken: () => Promise<string>;
  /** AppConfig (apiUrl 取得用) */
  config: AppConfig;
  /** テスト時に差し込む fetch 実装 */
  fetchImpl?: typeof fetch;
}

export class ChimesClient {
  private readonly options: ChimesClientOptions;

  constructor(options: ChimesClientOptions) {
    this.options = options;
  }

  /** GET /chimes (チャイムマスタ一覧を label 昇順で取得) */
  async list(): Promise<ChimeListResponse> {
    return this.request<ChimeListResponse>('GET', 'chimes');
  }

  /**
   * POST /chimes (チャイムを新規登録)
   *
   * @param label 表示名 (必須)
   * @param file MP3 ファイル (必須)。内部で base64 化して送信する。
   * @param durationMs 音源長 (ミリ秒 / 任意メタ情報)
   */
  async create(label: string, file: File, durationMs?: number): Promise<Chime> {
    const contentBase64 = await fileToBase64(file);
    const payload: ChimeCreateRequest = { label, content_base64: contentBase64 };
    if (typeof durationMs === 'number' && Number.isFinite(durationMs)) {
      payload.duration_ms = Math.round(durationMs);
    }
    return this.request<Chime>('POST', 'chimes', payload);
  }

  /**
   * PUT /chimes/{chime_id} (表示名 / 音源を更新)
   *
   * `label` / `file` のうち指定したものだけを更新する (未指定は現在値を維持)。
   */
  async update(
    chimeId: string,
    params: { label?: string; file?: File; durationMs?: number },
  ): Promise<Chime> {
    const payload: ChimeUpdateRequest = {};
    if (typeof params.label === 'string') {
      payload.label = params.label;
    }
    if (params.file) {
      payload.content_base64 = await fileToBase64(params.file);
    }
    if (typeof params.durationMs === 'number' && Number.isFinite(params.durationMs)) {
      payload.duration_ms = Math.round(params.durationMs);
    }
    return this.request<Chime>('PUT', `chimes/${encodeURIComponent(chimeId)}`, payload);
  }

  /**
   * DELETE /chimes/{chime_id}
   *
   * 放送地点から参照中のチャイムは API 側が 409 を返す (参照切れ防止)。
   * 呼び出し側は {@link ApiError} の status で 409 を判別してユーザーへ案内すること。
   */
  async remove(chimeId: string): Promise<ChimeDeleteResponse> {
    return this.request<ChimeDeleteResponse>('DELETE', `chimes/${encodeURIComponent(chimeId)}`);
  }

  /**
   * GET /chimes/{chime_id}/audio (試聴用に base64 MP3 を取得)
   *
   * 受け取った `content_base64` は `base64ToAudioUrl` で object URL に変換して
   * `<audio>` 要素などで再生できる。
   */
  async getAudio(chimeId: string): Promise<ChimeAudioResponse> {
    return this.request<ChimeAudioResponse>('GET', `chimes/${encodeURIComponent(chimeId)}/audio`);
  }

  // ---------------------------------------------------------------------
  // 内部実装
  // ---------------------------------------------------------------------

  private async request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const url = joinUrl(this.options.config.apiUrl, path);
    const idToken = await this.options.getIdToken();
    const fetchImpl = this.options.fetchImpl ?? fetch;

    const res = await fetchImpl(url, {
      method,
      headers: {
        ...(body !== undefined && { 'Content-Type': 'application/json' }),
        Authorization: `Bearer ${idToken}`,
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });

    if (!res.ok) {
      let errorBody: ApiErrorBody | undefined;
      try {
        errorBody = (await res.json()) as ApiErrorBody;
      } catch {
        // body が JSON で無い場合は無視
      }
      const message = errorBody?.message ?? `API request failed: HTTP ${res.status}`;
      throw new ApiError(res.status, message, errorBody);
    }

    return (await res.json()) as T;
  }
}
